import type React from "react";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useModalPresence } from "@/lib/hooks/use-modal-presence";
import { Dialog } from "../ui/Dialog";

interface DialogOptions {
  /**
   * The title of the dialog.
   */
  title: string;

  /**
   * The text content of the dialog.
   * If not provided, only the title will be shown.
   */
  text?: string;

  /**
   * The text for the confirm button.
   * @default "OK"
   */
  confirmLabel?: string;

  /**
   * The DS variant for the confirm button. Use "destructive" for delete/deactivate confirms.
   * @default "primary"
   */
  confirmVariant?: "primary" | "destructive";

  /**
   * The text for the cancel button.
   * If not provided, no cancel button will be shown.
   */
  cancelLabel?: string;

  /**
   * Whether to show a close button in the title bar.
   * @default false
   */
  withCloseButton?: boolean;

  /**
   * Additional class name for the dialog.
   */
  className?: string;

  /**
   * Whether the dialog is in a loading state.
   * When true, buttons are disabled and confirm button shows a spinner.
   * @default false
   */
  loading?: boolean;

  /**
   * Callback function to be called when the dialog is confirmed.
   */
  onConfirm?: () => Promise<void> | void;

  /**
   * Callback function to be called when the dialog is cancelled.
   */
  onCancel?: () => Promise<void> | void;
}

interface DialogState extends DialogOptions {
  onConfirm?: () => void;
  onCancel?: () => void;
}

interface DialogContextValue {
  show: (options: DialogOptions) => void;
  confirm: (options: DialogOptions) => void;
}

const DialogContext = createContext<DialogContextValue | null>(null);

/**
 * Provider component that manages dialog state and provides methods to show dialogs.
 */
export function DialogProvider({ children }: { children: React.ReactNode }) {
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [open, setOpen] = useState(false);
  // Drives the open/close microanimation: mounts the Dialog closed-first (so the enter transition
  // plays) and keeps it mounted through the exit before we tear the content down.
  const presence = useModalPresence(open);

  /**
   * Shows a dialog with the given options.
   */
  const show = useCallback((options: DialogOptions) => {
    setDialog(options);
    setOpen(true);
  }, []);

  /**
   * Shows a confirmation dialog.
   */
  const confirm = useCallback((options: DialogOptions) => {
    setDialog(options);
    setOpen(true);
  }, []);

  /** Closes the dialog; `useModalPresence` keeps it mounted while the exit transition plays. */
  const handleClose = useCallback(() => setOpen(false), []);

  // Release the retained dialog (and its onConfirm/onCancel closures) once the exit has finished.
  useEffect(() => {
    if (!presence.mounted) setDialog(null);
  }, [presence.mounted]);

  return (
    <DialogContext.Provider value={{ show, confirm }}>
      {children}
      {presence.mounted && dialog && (
        <Dialog {...dialog} opened={presence.opened} onClose={handleClose} />
      )}
    </DialogContext.Provider>
  );
}

/**
 * Hook to access dialog functionality.
 * Must be used within a DialogProvider.
 */
export function useDialog() {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error("useDialog must be used within a DialogProvider");
  }
  return context;
}
